import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import Logo from "@/components/shared/Logo";

export default function AdminLogin() {
  const { login, user, loading } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (!loading && user) return <Navigate to="/admin/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const r = await login(email.trim().toLowerCase(), password);
    setBusy(false);
    if (r.ok) {
      toast.success("Welcome back.");
      nav("/admin/dashboard");
    } else {
      toast.error(r.error || "Login failed.");
    }
  };

  return (
    <div className="min-h-screen bg-white text-[#0B2545] flex" data-testid="admin-login-page">
      {/* Left panel — navy */}
      <div className="hidden md:flex md:flex-1 bg-[#0B2545] text-white p-12 flex-col justify-between">
        <div>
          <Logo className="text-[28px]" tone="white" />
          <div className="overline-light mt-2">Admin</div>
        </div>
        <div>
          <h2 className="font-serif text-5xl md:text-6xl leading-tight text-white">
            Command center.
          </h2>
          <p className="mt-6 text-white/70 max-w-md">
            Sign in to view inquiries, filter leads, and update their status.
          </p>
        </div>
        <div className="overline-light">Internal team use only</div>
      </div>

      {/* Right panel — white form */}
      <div className="flex-1 flex items-center justify-center p-8 md:p-16 bg-white">
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md"
        >
          <div className="overline mb-3">Sign in</div>
          <h1 className="font-serif text-4xl md:text-5xl text-[#0B2545] leading-tight mb-10">
            Admin login.
          </h1>

          <div className="border border-line bg-white">
            <label className="block border-b border-line">
              <span className="overline px-5 pt-3 block">Email</span>
              <input
                required type="email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-email"
                className="w-full bg-transparent px-5 py-3 outline-none text-[#0B2545]"
                autoComplete="email"
              />
            </label>
            <label className="block">
              <span className="overline px-5 pt-3 block">Password</span>
              <input
                required type="password" value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password"
                className="w-full bg-transparent px-5 py-3 outline-none text-[#0B2545]"
                autoComplete="current-password"
              />
            </label>
          </div>

          <button
            type="submit" disabled={busy}
            className="btn-primary w-full justify-center mt-6 disabled:opacity-60"
            data-testid="admin-submit"
          >
            {busy ? "Signing in…" : "Sign in"}
            <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
          </button>

          <p className="mt-8 text-xs text-[#5B6472]">
            Internal use only. Contact ops for access.
          </p>
        </motion.form>
      </div>
    </div>
  );
}
